<template>
    <Transition>
        <div v-if="isVisible" ref="wrapper" @keydown="handleKeyDown">
            <slot></slot>
        </div>
    </Transition>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch, nextTick, onBeforeUnmount } from "vue";

const props = defineProps({
    becomeVisibleWhen: {
        type: Boolean,
        default: false
    },
    becomeVisibleIf: {
        type: Boolean,
        default: false
    },
    preventScroll: {
        type: Boolean,
        default: false
    }
});

const isVisible = ref(false);
const wrapper = ref<HTMLElement | null>(null);
const emit = defineEmits<{
    (e: "show"): void;
}>();

let lastInteractionWasKeyboard = false;

document.addEventListener("keydown", () => {
    lastInteractionWasKeyboard = true;
});

document.addEventListener("mousedown", () => {
    lastInteractionWasKeyboard = false;
});

function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter") {
        event.stopPropagation();
        event.preventDefault();
    }

    if (event.key === "Tab") {
        const currentInput = document.activeElement;
        if (currentInput instanceof HTMLInputElement && currentInput.type === "text") {
            performScroll();
        }
    }
}

function performScroll() {
    if (!props.preventScroll && wrapper.value) {
        wrapper.value.scrollIntoView({
            behavior: "smooth",
            block: "center"
        });
    }
}

const focusHandlers = new WeakMap<HTMLElement, (event: Event) => void>();

function setupFocusListeners() {
    if (!wrapper.value) return;

    const inputs = wrapper.value.querySelectorAll("input, select") as NodeListOf<HTMLInputElement | HTMLSelectElement>;
    inputs.forEach(input => {
        const handler = (event: Event) => {
            const target = event.target as HTMLElement;

            const isText =
                target instanceof HTMLInputElement &&
                target.type === "text";

            const isSelect = target instanceof HTMLSelectElement;

            if (isText || isSelect) performScroll();
        };

        focusHandlers.set(input, handler);
        input.addEventListener("focus", handler);
    });
}

function cleanupFocusListeners() {
    if (!wrapper.value) return;

    const inputs = wrapper.value.querySelectorAll("input, select") as NodeListOf<HTMLInputElement | HTMLSelectElement>;

    inputs.forEach(input => {
        const handler = focusHandlers.get(input);
        if (handler) input.removeEventListener("focus", handler);
    });
}

async function focusAndScrollToInput() {
    await nextTick();

    setTimeout(async () => {
        if (!wrapper.value) return;

        const input = wrapper.value.querySelector("input, select") as HTMLInputElement | HTMLSelectElement | null;

        if (!input) return;

        const isTextInput = input instanceof HTMLInputElement && input.type === "text";
        const currentInput = document.activeElement;
        const isCurrentTextInput = currentInput instanceof HTMLInputElement && currentInput.type === "text";

        if (isTextInput && isCurrentTextInput) return;

        performScroll();

        const isCurrentlyFocused = document.activeElement === input;
        if (!isCurrentlyFocused) {
            input.focus({ preventScroll: true });
        }
    }, 50);
}

onMounted(() => {
    if (props.becomeVisibleWhen || props.becomeVisibleIf) {
        isVisible.value = true;
    }
    setupFocusListeners();
});

onBeforeUnmount(() => {
    cleanupFocusListeners();
});

watch(
    () => isVisible.value,
    newValue => {
        if (newValue) {
            nextTick(() => {
                setupFocusListeners();
            });
        }
    }
);

watch(
    () => props.becomeVisibleWhen,
    async (newValue, oldValue) => {
        if (newValue && !oldValue) {
            isVisible.value = true;
            emit("show");

            const currentElement = document.activeElement;
            const isRadio = currentElement instanceof HTMLInputElement && currentElement.type === "radio";
            const isInsideRadioGroup = currentElement && currentElement.closest('[role="radiogroup"]');

            const isInteractingWithForm =
                currentElement &&
                (
                    currentElement instanceof HTMLInputElement ||
                    currentElement instanceof HTMLSelectElement ||
                    currentElement instanceof HTMLTextAreaElement ||
                    currentElement instanceof HTMLButtonElement
                );

            if (isRadio || isInsideRadioGroup) {
                if (lastInteractionWasKeyboard) return;
                await focusAndScrollToInput();
                return;
            }

            if (!isInteractingWithForm) await focusAndScrollToInput();
        }
    }
);

watch(
    () => props.becomeVisibleIf,
    async newValue => {
        isVisible.value = newValue;
        emit("show");

        if (newValue) {
            const currentElement = document.activeElement;
            const isInteractingWithForm =
                currentElement &&
                (
                    currentElement instanceof HTMLInputElement ||
                    currentElement instanceof HTMLSelectElement ||
                    currentElement instanceof HTMLTextAreaElement ||
                    currentElement instanceof HTMLButtonElement ||
                    currentElement.closest('[role="radiogroup"]') !== null
                );

            if (!isInteractingWithForm) await focusAndScrollToInput();
        }
    }
);
</script>
